import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { API_STATUS } from '@/constants';
import { COMMON_ERROR_DIALOG_CODE } from '@/constants/error';
import { SERIAL_AUTH_API_RESULT_CODE, useSerialAuthApi } from '@/features/api';
import { useAuthSerial } from '@/hooks/useAuthSerial';
import { useCommonErrorDialog } from '@/hooks/useCommonErrorDialog';
import { useLoginUserInfo } from '@/hooks/useLoginUserInfo';

import {
  SERIAL_CHANGE_CONTENT,
  SERIAL_LINK_STATUS,
  SERIAL_REGISTRATION_CONTENT,
  SerialErrorMessageMap,
  SerialLinkForm,
  SerialLinkStatusType,
} from '../constants';

/**
 * シリアル連携の成功時に実行したい処理
 */
export type SuccessFunction = () => void;

/**
 * プロパティ
 */
type Props = {
  // シリアル連携の成功時に実行したい処理
  onSuccessFun?: SuccessFunction;
};

/**
 * シリアル連携ダイアログ hooks
 *
 * @returns
 */
export const useSerialLinkDialog = ({ onSuccessFun }: Props) => {
  const { serialAuthState, fetchSerialAuth } = useSerialAuthApi();
  const { setCommonErrorDialogType } = useCommonErrorDialog();
  const { isFreePlan } = useLoginUserInfo();
  const { serial } = useAuthSerial();

  /**
   * プロパティに指定された関数Ref
   */
  const onSuccessFuncRef = useRef<SuccessFunction>();
  useEffect(() => {
    onSuccessFuncRef.current = onSuccessFun;
  }, [onSuccessFun]);

  /**
   * シリアル連携状態
   */
  const [serialLinkStatusType, setSerialLinkStatusType] =
    useState<SerialLinkStatusType>(SERIAL_LINK_STATUS.NONE);

  /**
   * シリアル連携ダイアログ内に表示するコンテンツ
   */
  const serialLinkDialogContent = useMemo(() => {
    if (isFreePlan) {
      return SERIAL_REGISTRATION_CONTENT;
    }

    return SERIAL_CHANGE_CONTENT;
  }, [isFreePlan]);

  /**
   * シリアル連携エラーメッセージ
   */
  const serialLinkErrorMessage = useMemo(
    () => SerialErrorMessageMap[serialLinkStatusType],
    [serialLinkStatusType],
  );

  /**
   * シリアル連携状態をリセット
   */
  const resetSerialLinkStatusType = useCallback(() => {
    setSerialLinkStatusType(SERIAL_LINK_STATUS.NONE);
  }, []);

  /**
   * [シリアル登録/更新]ボタンのクリック処理
   */
  const onClickSerialRegUpdate = useCallback(
    (inputParam: SerialLinkForm) => {
      // シリアル認証API呼び出し
      fetchSerialAuth({ serial: inputParam.serialLink }, true);
    },
    [fetchSerialAuth],
  );

  /**
   * シリアル認証APIの結果を監視し、連携状態を更新
   */
  useEffect(() => {
    if (serialAuthState.status === API_STATUS.LOADING) {
      return;
    }

    if (!serialAuthState.data) {
      setSerialLinkStatusType(SERIAL_LINK_STATUS.NONE);

      return;
    }

    switch (serialAuthState.data.resultCode) {
      case SERIAL_AUTH_API_RESULT_CODE.OK:
        setSerialLinkStatusType(SERIAL_LINK_STATUS.SUCCESS);
        // 成功時にプロパティに指定された関数を実行
        onSuccessFuncRef.current?.();
        break;
      case SERIAL_AUTH_API_RESULT_CODE.INFO_INVALID_LICENSE:
        setSerialLinkStatusType(SERIAL_LINK_STATUS.INFO_INVALID_LICENSE);
        break;
      case SERIAL_AUTH_API_RESULT_CODE.INFO_EXPIRED_LICENSE:
        setSerialLinkStatusType(SERIAL_LINK_STATUS.INFO_EXPIRED_LICENSE);
        break;
      case SERIAL_AUTH_API_RESULT_CODE.INFO_ALREADY_LINKED:
        setSerialLinkStatusType(SERIAL_LINK_STATUS.INFO_ALREADY_LINKED);
        break;
      case SERIAL_AUTH_API_RESULT_CODE.INFO_EXPIRED_IDTOKEN:
        setCommonErrorDialogType(COMMON_ERROR_DIALOG_CODE.PTID_ERROR);
        break;
      default:
        setSerialLinkStatusType(SERIAL_LINK_STATUS.ERROR);
    }
  }, [serialAuthState.data, serialAuthState.status, setCommonErrorDialogType]);

  /**
   * 入力欄に表示するデフォルトのシリアルコード
   */
  const defaultSerial = useMemo(() => {
    if (serial) {
      return serial;
    }

    return '';
  }, [serial]);

  return {
    onClickSerialRegUpdate,
    serialLinkErrorMessage,
    serialAuthState,
    setSerialLinkStatusType,
    resetSerialLinkStatusType,
    defaultSerial,
    serialLinkDialogContent,
  };
};
